import isNil from 'lodash/isNil';
import parse from 'node-html-parser';

import type {
  DataAttributeFilterRule,
  FilterHtmlByDataAttributesProps,
} from './filter-html-by-data-attributes.types';
import type { HTMLElement } from 'node-html-parser';

/**
 * Filters HTML content by removing elements with specific data attributes
 *
 * @param html - The HTML string to filter
 * @param options - Configuration for filtering rules
 * @returns Filtered HTML string with matching elements removed
 *
 * @example
 * const filtered = filterHtmlByDataAttributes({
 *   html: '<p>Keep this</p><div data-collapsible="true">Remove this</div>',
 *   options: {
 *     rules: [{ attribute: 'collapsible', value: 'true' }]
 *   }
 * });
 * // Returns: '<p>Keep this</p>'
 */
function filterHtmlByDataAttributes({
  html,
  options,
}: FilterHtmlByDataAttributesProps): string {
  const { rules, preserveStructure = true } = options;

  // Parse HTML string into AST
  const parsed = parse(html, {
    parseNoneClosedTags: true,
    blockTextElements: {
      script: true,
      style: true,
    },
  });

  /**
   * Helper function to check if element matches any filter rule
   */
  const shouldRemoveElement = (element: HTMLElement): boolean => {
    return rules.some((rule: DataAttributeFilterRule) => {
      const attrValue = element.getAttribute(`data-${rule.attribute}`);

      // If attribute doesn't exist, no match
      if (isNil(attrValue)) {
        return false;
      }

      // If no value specified in rule, match any value
      if (rule.value === undefined) {
        return true;
      }

      // Otherwise, match specific value
      return attrValue === rule.value;
    });
  };

  /**
   * Recursively filter elements from the DOM tree
   */
  const filterElements = (node: HTMLElement) => {
    if (!node.childNodes) return;

    // Process children in reverse to avoid index issues when removing
    for (let i = node.childNodes.length - 1; i >= 0; i--) {
      const child = node.childNodes[i] as HTMLElement;

      // Skip text nodes (they don't have tagName)
      if (child.nodeType === 3) continue;

      // Check if this element should be removed
      if (shouldRemoveElement(child)) {
        child.remove();
      } else {
        // Recursively check children
        filterElements(child);
      }
    }
  };

  // Start filtering from root
  filterElements(parsed);

  // Remove whitespace if preserving structure
  if (preserveStructure) {
    parsed.removeWhitespace();
  }

  return parsed.toString();
}

export default filterHtmlByDataAttributes;
